import type { Locale } from '@shared/lib/i18n/locales';

/*
 * Legal texts served at /legal/*. The source of truth is docs/legal/<file>.<locale>.md; the copies in
 * ./content are bundled with the app and must stay byte-identical (legal.test.tsx enforces it).
 * The version is the value the registration flow records as accepted_terms_version.
 */
export const LEGAL_VERSION = '2026-10-01';

export const LEGAL_DOCS = [
  { slug: 'terms', file: 'terms-of-use' },
  { slug: 'privacy', file: 'privacy-policy' },
  { slug: 'cookies', file: 'cookie-notice' },
  { slug: 'acceptable-use', file: 'acceptable-use' },
] as const;

export type LegalSlug = (typeof LEGAL_DOCS)[number]['slug'];
export type LegalFile = (typeof LEGAL_DOCS)[number]['file'];

export function isLegalSlug(value: unknown): value is LegalSlug {
  return LEGAL_DOCS.some((d) => d.slug === value);
}

export function legalFile(slug: LegalSlug): LegalFile {
  return LEGAL_DOCS.find((d) => d.slug === slug)?.file ?? 'terms-of-use';
}

// One lazy chunk per document and locale.
const loaders = import.meta.glob<string>('./content/*.md', { query: '?raw', import: 'default' });

export function legalContentPath(file: LegalFile, locale: Locale): string {
  return `./content/${file}.${locale}.md`;
}

export async function loadLegalDocument(slug: LegalSlug, locale: Locale): Promise<string> {
  const load = loaders[legalContentPath(legalFile(slug), locale)] ?? loaders[legalContentPath(legalFile(slug), 'en')];
  if (!load) throw new Error(`Missing legal document ${slug}/${locale}`);
  return load();
}

/** Bracketed founder placeholders such as [COMPANY NAME] — while any remain the page shows a draft banner. */
export const PLACEHOLDER = /\[[A-Z][A-Z0-9 _/.-]{2,}\]/g;

export function placeholdersIn(markdown: string): string[] {
  return [...new Set(markdown.match(PLACEHOLDER) ?? [])];
}

/** Splits the leading "# Title" line from the body. */
export function splitTitle(markdown: string): { title: string; body: string } {
  const text = markdown.charCodeAt(0) === 0xfeff ? markdown.slice(1) : markdown;
  const match = /^\s*#\s+(.+)\n?/.exec(text);
  if (!match) return { title: '', body: text };
  return { title: (match[1] ?? '').trim(), body: text.slice(match[0].length) };
}
